import React, { useMemo } from 'react';
import { useI18n } from '../../../i18n';
import {
  guardTimeline,
  markChanges,
  type RosterSnapshot,
  type TimelineSegment,
} from '../../../services/rosterCompliance';
import { MAX_ABSOLUTE_MINUTE } from '../../../utils/siteScheduling';
import { formatClock, formatRange, type Translator } from './fixPresentation';

interface RosterDiffProps {
  id?: string;
  before: RosterSnapshot;
  after: RosterSnapshot;
  guardIds: string[];
  guardName: (guardId: string) => string;
}

interface GuardDiffRow {
  guardId: string;
  before: TimelineSegment[];
  after: TimelineSegment[];
}

interface Axis {
  start: number;
  end: number;
  ticks: number[];
}

const HOUR = 60;
const MIN_SPAN = 6 * HOUR;

/** Frames the axis on the hours that matter, an hour either side of the work shown. */
function buildAxis(rows: GuardDiffRow[]): Axis {
  const segments = rows.flatMap((row) => [...row.before, ...row.after]);
  const earliest = segments.length > 0 ? Math.min(...segments.map((s) => s.startMinute)) : 0;
  const latest = segments.length > 0 ? Math.max(...segments.map((s) => s.endMinute)) : 24 * HOUR;

  let start = Math.max(0, Math.floor((earliest - HOUR) / HOUR) * HOUR);
  let end = Math.min(MAX_ABSOLUTE_MINUTE, Math.ceil((latest + HOUR) / HOUR) * HOUR);
  if (end - start < MIN_SPAN) {
    end = Math.min(MAX_ABSOLUTE_MINUTE, start + MIN_SPAN);
    start = Math.max(0, end - MIN_SPAN);
  }

  const span = end - start;
  const step = span <= 8 * HOUR ? HOUR : span <= 16 * HOUR ? 2 * HOUR : 3 * HOUR;
  const ticks: number[] = [];
  for (let tick = Math.ceil(start / step) * step; tick <= end; tick += step) ticks.push(tick);
  return { start, end, ticks };
}

const percent = (minute: number, axis: Axis) => `${((minute - axis.start) / (axis.end - axis.start)) * 100}%`;

function describeTimeline(label: string, segments: TimelineSegment[], tr: Translator): string {
  if (segments.length === 0) return tr.t('{label}: not rostered', { label });
  const parts = segments.map((segment) => (
    segment.type === 'duty'
      ? tr.t('{duty} {range}', { duty: segment.label, range: formatRange(segment.startMinute, segment.endMinute) })
      : tr.t('break {range}', { range: formatRange(segment.startMinute, segment.endMinute) })
  ));
  return `${label}: ${parts.join(', ')}`;
}

function TimelineTrack({ label, segments, axis }: { label: string; segments: TimelineSegment[]; axis: Axis }) {
  const { t, tn } = useI18n();
  const tr = { t, tn };
  return (
    <div className="cfx-diff__row">
      <span className="cfx-diff__row-label" aria-hidden="true">{label}</span>
      <div className="cfx-diff__track" role="img" aria-label={describeTimeline(label, segments, tr)}>
        {axis.ticks.map((tick) => (
          <span key={tick} className="cfx-diff__gridline" style={{ left: percent(tick, axis) }} aria-hidden="true" />
        ))}
        {segments.map((segment) => (
          <span
            key={`${segment.key}:${segment.changed}`}
            className={[
              'cfx-diff__segment',
              `cfx-diff__segment--${segment.type}`,
              segment.changed ? 'is-changed' : '',
            ].filter(Boolean).join(' ')}
            style={{
              left: percent(Math.max(segment.startMinute, axis.start), axis),
              width: percent(axis.start + (Math.min(segment.endMinute, axis.end) - Math.max(segment.startMinute, axis.start)), axis),
            }}
            title={segment.type === 'duty'
              ? `${segment.label} ${formatRange(segment.startMinute, segment.endMinute)}`
              : t('break {range}', { range: formatRange(segment.startMinute, segment.endMinute) })}
            aria-hidden="true"
          >
            {segment.type === 'duty' && <span className="cfx-diff__segment-label">{segment.label}</span>}
          </span>
        ))}
        {segments.length === 0 && <span className="cfx-diff__empty" aria-hidden="true">{t('Not rostered')}</span>}
      </div>
    </div>
  );
}

/** Before and after for every guard a change set touches, on one shared time axis. */
export default function RosterDiff({ id, before, after, guardIds, guardName }: RosterDiffProps) {
  const { t } = useI18n();

  const rows = useMemo<GuardDiffRow[]>(() => guardIds.map((guardId) => {
    const beforeSegments = guardTimeline(before, guardId);
    const afterSegments = guardTimeline(after, guardId);
    return {
      guardId,
      before: markChanges(beforeSegments, afterSegments),
      after: markChanges(afterSegments, beforeSegments),
    };
  }), [after, before, guardIds]);

  const axis = useMemo(() => buildAxis(rows), [rows]);

  return (
    <div id={id} className="cfx-diff">
      <ul className="cfx-diff__legend" aria-label={t('Legend')}>
        <li><span className="cfx-diff__swatch cfx-diff__swatch--duty" aria-hidden="true" />{t('Duty')}</li>
        <li><span className="cfx-diff__swatch cfx-diff__swatch--break" aria-hidden="true" />{t('Break')}</li>
        <li><span className="cfx-diff__swatch cfx-diff__swatch--changed" aria-hidden="true" />{t('Changed')}</li>
      </ul>

      <div className="cfx-diff__axis" aria-hidden="true">
        <span className="cfx-diff__row-label" />
        <div className="cfx-diff__ticks">
          {axis.ticks.map((tick) => (
            <span key={tick} className="cfx-diff__tick" style={{ left: percent(tick, axis) }}>{formatClock(tick)}</span>
          ))}
        </div>
      </div>

      <ul className="cfx-diff__guards">
        {rows.map((row) => (
          <li key={row.guardId} className="cfx-diff__guard">
            <p className="cfx-diff__guard-name">{guardName(row.guardId) || t('Unknown guard')}</p>
            <TimelineTrack label={t('Before')} segments={row.before} axis={axis} />
            <TimelineTrack label={t('After')} segments={row.after} axis={axis} />
          </li>
        ))}
      </ul>

      <p className="cfx-diff__footnote">{t('Everyone else on this roster is unchanged.')}</p>
    </div>
  );
}
